import React from 'react';

import { BloodworkEntry } from '../components/bloodworkz/bloodwork-entry';
import { BloodworkDisplay } from '../components/bloodworkz/bloodwork-display';

import { observer, inject } from 'mobx-react';
import { BloodworkEntryStore } from '../stores/bloodwork-entry-store';
import { UserStore } from '../stores/user-store';
import { ClientEntryStore } from '../stores/client-entry-store';
import { FurtherConsiderationsEntryStore } from '../stores/further-considerations-entry-store';
import { UserProfileUIStore } from '../stores/user-profile-ui-store';
import { Client } from '../models/Client';
import { scrollPanelIntoView } from '../utils/scrollPanelIntoView';
import { v4 as uuidv4 } from 'uuid';
import { Container } from 'react-bootstrap';

/**
 * Reopen the entry panel when the practitioner asks for this page while already
 * standing on it.
 *
 * "Add Test Results" appears twice, in the button row and in the Test Results
 * menu, and both are plain links to /bloodwork/add. Clicking either from this
 * page pushes the same route again, which gives React Router a fresh
 * location.key and changes nothing else: the container never unmounts, so
 * componentDidMount does not re-run and the collapse left behind by the last
 * save stayed exactly where it was. The button sat there looking pressed and
 * doing nothing, and the chevron was the only way back into the form.
 *
 * Keyed on location.key, so it fires on navigation and NOT on the incidental
 * re-renders — this container observes the profile, the ranges, the client list
 * and the further considerations, and re-renders as each one lands. None of
 * those may reopen a panel the practitioner just closed.
 *
 * Only the panel is reopened. The form is deliberately NOT cleared, because this
 * is just as easily a mis-click while a test is half entered, and there is no
 * undo for throwing that away.
 *
 * It covers the two Edit routes on the same terms, which is what they already
 * wanted: clicking Edit on a second test while this page is open pushes
 * /bloodwork/edit again, and that should land on the form rather than leave it
 * out of sight.
 *
 * Exported for its test: @inject wraps the component, so the class itself is not
 * reachable from outside this module.
 */
export function reopenEntryPanelOnRenavigation(
  previousLocation: any,
  location: any,
  bloodworkEntryStore: BloodworkEntryStore,
): void {
  if (previousLocation?.key === location?.key) {
    return;
  }
  bloodworkEntryStore.setHideEnterTestResults(false);
  // Both entry points can be clicked from a page scrolled well down (the menu
  // lives in the sticky header, so it is reachable from anywhere), and expanding
  // a form the practitioner cannot see reads as one more click that did nothing.
  // Same helper the panels use after a save, for the same reason: the panel is a
  // different height once open, so anchor on its element rather than on a
  // captured offset.
  scrollPanelIntoView('add-test-results-panel');
}

interface BloodworkEntryContainerProps {
  match?: any;
  /** From <Route>. Read for its key; see componentDidUpdate. */
  location?: any;
  bloodworkEntryStore?: BloodworkEntryStore;
  userStore?: UserStore;
  userProfileUIStore?: UserProfileUIStore;
  clientEntryStore?: ClientEntryStore;
  furtherConsiderationsEntryStore?: FurtherConsiderationsEntryStore;
  history?: any;
  path?: any;
}

@inject(
  'bloodworkEntryStore',
  'userStore',
  'userProfileUIStore',
  'clientEntryStore',
  'furtherConsiderationsEntryStore',
)
@observer
export class BloodworkEntryContainer extends React.Component<
  BloodworkEntryContainerProps,
  {}
> {
  componentDidMount() {
    const {
      userProfileUIStore,
      bloodworkEntryStore,
      clientEntryStore,
      furtherConsiderationsEntryStore,
    } = this.props;
    userProfileUIStore.loadCurrentUserProfile();
    bloodworkEntryStore.loadBloodworkRanges();
    clientEntryStore.loadCurrentClients();
    furtherConsiderationsEntryStore.loadFurtherConsiderations();
  }

  componentDidUpdate(previousProps: BloodworkEntryContainerProps) {
    reopenEntryPanelOnRenavigation(
      previousProps.location,
      this.props.location,
      this.props.bloodworkEntryStore,
    );
  }

  findClient(clientId: string): Client {
    return clientId && this.props.clientEntryStore.clients
      ? Client.findClient(clientId, this.props.clientEntryStore.clients)
      : new Client();
  }

  render() {
    const {
      bloodworkEntryStore,
      userStore,
      userProfileUIStore,
      clientEntryStore,
      furtherConsiderationsEntryStore,
      match,
    } = this.props;

    return (
      <Container fluid>
        <BloodworkEntry
          hideEnterTestResults={bloodworkEntryStore.hideEnterTestResults}
          selectedClient={this.findClient(match.params.clientId)}
          currentUser={userStore.currentUser}
          currentUserProfile={userProfileUIStore.currentUserProfile}
          currentClients={clientEntryStore.clients}
          bloodwork={bloodworkEntryStore.currentBloodwork}
          bloodworkRanges={bloodworkEntryStore.effectiveRanges}
          bloodworkEntryStore={bloodworkEntryStore}
          refreshBloodworks={bloodworkEntryStore.refreshBloodworks}
          updateClient={(client) => clientEntryStore.updateClientFields(client)}
          learnCustomMarkers={(library) => {
            userProfileUIStore.updateCustomMarkerLibrary(library);
            return userProfileUIStore.saveCurrentUserProfile();
          }}
          createCustomMarkerSection={(name) =>
            userProfileUIStore.addCustomMarkerSection(name, uuidv4())
          }
          renameCustomMarkerSection={(from, to) =>
            userProfileUIStore.renameCustomMarkerSection(from, to, uuidv4())
          }
          history={this.props.history}
          path={this.props.path}
        />

        <BloodworkDisplay
          currentUserProfile={userProfileUIStore.currentUserProfile}
          testResultsPanelLabel="Test Results"
          bloodworks={bloodworkEntryStore.bloodworks}
          bloodworkEntryStore={bloodworkEntryStore}
          refreshBloodworks={bloodworkEntryStore.refreshBloodworks}
          furtherConsiderations={
            furtherConsiderationsEntryStore.furtherConsiderations
          }
          // The list is what fetches it: BloodworkDisplay loads the tests in
          // componentDidMount only when this is set, and renders nothing at all
          // when it has none. Without it the card was here or not depending on
          // whether the dashboard had already filled the store, so a
          // practitioner who came straight to this page never saw it.
          allowLoadMoreTestResults={true}
          history={this.props.history}
          showAnalyzeTableButton={true}
          updateClient={(client) => clientEntryStore.updateClientFields(client)}
          currentClients={clientEntryStore.clients}
        />
      </Container>
    );
  }
}
